import { AgentStreamRequest } from '@Models';
import { getAccessToken } from '@Store';
import { AttachmentUploadError } from '../../Helpers/Agents/attachments';

// ============================================================================
// Low-level streaming functions (SSE and NDJSON)
// ============================================================================

// Types
/**
 * Represents structured SSE event data
 */
interface SSEEventData {
  readonly eventId: string;
  readonly eventType: string;
  readonly data: string;
}

/**
 * Represents a parsed SSE line with type and value
 */
interface SSELineData {
  readonly type: 'id' | 'event' | 'data';
  readonly value: string;
}

/**
 * Represents fallback event data for unknown event types
 */
interface FallbackEventData {
  readonly type?: string;
  readonly text?: string;
  readonly data?: string;
}

/**
 * Supported SSE event types
 */
type SSEEventType = 'chunk' | 'trace' | 'error' | 'done';

// Constants
/** SSE event separator used to delimit events */
const SSE_EVENT_SEPARATOR = '\n\n' as const;
/** Length of the SSE event separator */
const SSE_EVENT_SEPARATOR_LENGTH = 2 as const;
/** Default conversation ID for agent stream requests */
const DEFAULT_CONVERSATION_ID = 'default-conversation' as const;
/** Default maximum prompt length for agent stream requests */
const DEFAULT_MAX_PROMPT_LENGTH = 10000 as const;
/** SSE line prefixes for parsing */
const SSE_LINE_PREFIXES = {
  ID: 'id:',
  EVENT: 'event:',
  DATA: 'data:',
} as const;
/** Stream endpoint for the agent runner */
const STREAM_ENDPOINT = 'agent/aws-runner/run/stream' as const;

/**
 * Creates the request body for agent streaming requests
 * @param request - The agent stream request parameters
 * @returns Formatted request body with default values applied
 */
const createRequestBody = (request: AgentStreamRequest) => ({
  prompt: request.prompt,
  conversation_id: request.conversation_id || DEFAULT_CONVERSATION_ID,
  max_prompt_length: request.max_prompt_length || DEFAULT_MAX_PROMPT_LENGTH,
  trace: true,
  source_name: request.source_name,
});

/**
 * Parses a single SSE line to extract type and value
 * @param line - The SSE line to parse (e.g., "data: hello world")
 * @returns Parsed line data or null if line doesn't match SSE format
 */
const parseSSELine = (line: string): SSELineData | null => {
  if (line.startsWith(SSE_LINE_PREFIXES.ID)) {
    return { type: 'id', value: line.substring(SSE_LINE_PREFIXES.ID.length).trim() };
  }
  if (line.startsWith(SSE_LINE_PREFIXES.EVENT)) {
    return { type: 'event', value: line.substring(SSE_LINE_PREFIXES.EVENT.length).trim() };
  }
  if (line.startsWith(SSE_LINE_PREFIXES.DATA)) {
    return { type: 'data', value: line.substring(SSE_LINE_PREFIXES.DATA.length) };
  }
  return null;
};

/**
 * Parses a complete SSE event from raw event text
 * @param eventText - Raw SSE event text containing id, event, and data lines
 * @returns Structured SSE event data
 */
const parseSSEEvent = (eventText: string): SSEEventData => {
  const lines = eventText.split('\n');

  const eventData = lines.reduce(
    (acc, line) => {
      const parsed = parseSSELine(line);
      if (!parsed) return acc;

      switch (parsed.type) {
        case 'id':
          return { ...acc, eventId: parsed.value };
        case 'event':
          return { ...acc, eventType: parsed.value };
        case 'data':
          return { ...acc, dataLines: [...acc.dataLines, parsed.value] };
        default:
          return acc;
      }
    },
    { eventId: '', eventType: '', dataLines: [] as string[] },
  );

  return {
    eventId: eventData.eventId,
    eventType: eventData.eventType,
    data: eventData.dataLines.join('\n'),
  };
};

/**
 * Handles chunk events by calling the onChunk callback
 * @param data - The chunk data received from the stream
 * @param onChunk - Callback function to handle chunk data
 */
const handleChunkEvent = (data: string, onChunk: (text: string) => void): void => {
  onChunk(data);
};

/**
 * Handles trace events by parsing JSON data and calling the onTrace callback
 * @param data - The trace data received from the stream
 * @param onTrace - Optional callback function to handle trace data
 */
const handleTraceEvent = (data: string, onTrace?: (trace: unknown) => void): void => {
  if (!onTrace) return;

  try {
    const traceData: unknown = JSON.parse(data);
    onTrace(traceData);
  } catch {
    onTrace(data);
  }
};

/**
 * Handles error events by throwing an error with the received message
 * @param data - The error message received from the stream
 * @throws {Error} Always throws an error with the received message
 */
const handleErrorEvent = (data: string): never => {
  // Try to parse the error message if it's a JSON string
  let errorMessage = data || 'Streaming error';

  try {
    // The error data might be a JSON string, try to parse it
    const parsed: unknown = JSON.parse(data);
    errorMessage = typeof parsed === 'string' ? parsed : data;
  } catch {
    // If parsing fails, use the raw data
    errorMessage = data;
  }

  throw new Error(errorMessage);
};

/**
 * Handles fallback events for unknown event types by attempting to parse as JSON
 * @param data - The fallback data received from the stream
 * @param onChunk - Callback function to handle chunk data if parsing succeeds
 */
const handleFallbackEvent = (data: string, onChunk: (text: string) => void): void => {
  if (!data) return;

  try {
    const parsed = JSON.parse(data) as FallbackEventData;
    if (parsed?.type === 'chunk') {
      onChunk(parsed.text || parsed.data || '');
    }
  } catch {
    // Silently ignore parsing errors for fallback
  }
};

/**
 * Processes a complete SSE event by routing it to the appropriate handler
 * @param eventText - The complete SSE event text
 * @param onChunk - Callback function for chunk events
 * @param onTrace - Optional callback function for trace events
 */
const processSSEEvent = (
  eventText: string,
  onChunk: (text: string) => void,
  onTrace?: (trace: unknown) => void,
): void => {
  const { eventType, data } = parseSSEEvent(eventText);
  const eventTypeAsEnum = eventType as SSEEventType;

  switch (eventTypeAsEnum) {
    case 'chunk':
      handleChunkEvent(data, onChunk);
      break;
    case 'trace':
      handleTraceEvent(data, onTrace);
      break;
    case 'error':
      handleErrorEvent(data);
      break;
    case 'done':
      return;
    default:
      handleFallbackEvent(data, onChunk);
      break;
  }
};

/**
 * Extracts a complete SSE event from the buffer
 * @param buffer - The current buffer containing partial SSE events
 * @returns Object with complete event text and remaining buffer, or null if no complete event found
 */
const extractCompleteEvent = (buffer: string): { eventText: string; remainingBuffer: string } | null => {
  const eventEndIndex = buffer.indexOf(SSE_EVENT_SEPARATOR);

  if (eventEndIndex === -1) {
    return null;
  }

  return {
    eventText: buffer.substring(0, eventEndIndex),
    remainingBuffer: buffer.substring(eventEndIndex + SSE_EVENT_SEPARATOR_LENGTH),
  };
};

/**
 * Processes the buffer to extract and handle complete SSE events
 * @param buffer - The current buffer containing partial SSE events
 * @param onChunk - Callback function for chunk events
 * @param onTrace - Optional callback function for trace events
 * @returns The remaining buffer after processing complete events
 */
const processBuffer = (buffer: string, onChunk: (text: string) => void, onTrace?: (trace: unknown) => void): string => {
  let remainingBuffer = buffer;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const result = extractCompleteEvent(remainingBuffer);

    if (!result) {
      break;
    }

    const { eventText, remainingBuffer: newBuffer } = result;
    remainingBuffer = newBuffer;

    if (eventText.trim()) {
      processSSEEvent(eventText, onChunk, onTrace);
    }
  }

  return remainingBuffer;
};

/**
 * Creates fetch options for the SSE request
 * @param accessToken - The authentication token
 * @param requestBody - The request body to send
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Fetch options configured for SSE
 */
const createFetchOptions = (accessToken: string, requestBody: object, signal?: AbortSignal): RequestInit => ({
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    Authorization: `Bearer ${accessToken}`,
  },
  body: JSON.stringify(requestBody),
  signal,
});

/**
 * Validates the HTTP response for errors
 * @param response - The fetch response to validate
 * @throws {Error} Throws an error if the response is not ok
 */
const validateResponse = (response: Response): void => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
};

/**
 * Gets the stream reader from the response body
 * @param response - The fetch response containing the stream
 * @returns The readable stream reader
 * @throws {Error} Throws an error if no response body is available
 */
const getStreamReader = (response: Response): ReadableStreamDefaultReader<Uint8Array> => {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body available for streaming');
  }
  return reader;
};

/**
 * Processes stream chunks by reading from the stream and handling SSE events
 * @param reader - The stream reader to read chunks from
 * @param decoder - Text decoder to convert bytes to text
 * @param onChunk - Callback function for chunk events
 * @param onTrace - Optional callback function for trace events
 */
const processStreamChunks = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder,
  onChunk: (text: string) => void,
  onTrace?: (trace: unknown) => void,
): Promise<void> => {
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    const chunk = decoder.decode(value, { stream: true });
    buffer += chunk;

    buffer = processBuffer(buffer, onChunk, onTrace);
  }
};

/**
 * Main function to stream agent responses using Server-Sent Events.
 * Establishes a connection, processes the stream, and calls appropriate callbacks.
 *
 * @param baseApiAddress - The base API address for the request
 * @param request - The agent stream request parameters
 * @param onChunk - Callback function called for each text chunk received
 * @param onTrace - Optional callback function called for trace data
 * @param signal - Optional AbortSignal to cancel the request
 * @throws {Error} When the connection fails, response is invalid, or streaming errors occur
 */
const streamAgent = async (
  baseApiAddress: string,
  request: AgentStreamRequest,
  onChunk: (text: string) => void,
  onTrace?: (trace: unknown) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const accessToken = await getAccessToken();
  const requestBody = createRequestBody(request);
  const fetchOptions = createFetchOptions(accessToken, requestBody, signal);

  const response = await fetch(`${baseApiAddress}${STREAM_ENDPOINT}`, fetchOptions);
  validateResponse(response);

  const reader = getStreamReader(response);
  const decoder = new TextDecoder();

  try {
    await processStreamChunks(reader, decoder, onChunk, onTrace);
  } finally {
    reader.releaseLock();
  }
};

/**
 * Structured event from NDJSON stream - Chunk type
 */
export interface StreamEventChunk {
  readonly type: 'chunk';
  readonly text: string;
}

/**
 * Structured event from NDJSON stream - Trace type
 * Backend now sends processed event_type and translated message
 */
export interface StreamEventTrace {
  readonly type: 'trace';
  readonly event_type: string;
  readonly input_tokens?: number;
  readonly output_tokens?: number;
}

/**
 * Structured event from NDJSON stream - Progress type.
 * Model narration for the thinking panel; never part of the answer.
 */
export interface StreamEventProgress {
  readonly type: 'progress';
  readonly text: string;
}

/**
 * Structured event from NDJSON stream - Retract type.
 * Discards the answer text streamed so far in this turn (it was narration).
 */
export interface StreamEventRetract {
  readonly type: 'retract';
}

/**
 * Structured event from NDJSON stream - Done type
 */
export interface StreamEventDone {
  readonly type: 'done';
  readonly metrics: {
    input_tokens: number;
    output_tokens: number;
    latency: number;
    cached?: boolean;
  };
}

/**
 * Structured event from NDJSON stream - Error type
 */
export interface StreamEventError {
  readonly type: 'error';
  readonly error: string;
}

/**
 * Structured event from NDJSON stream - Files type.
 * AgentCore emits files already on S3 (s3_uri present).
 */
export interface StreamEventFile {
  readonly name: string;
  readonly type?: string | null;
  readonly size?: number;
  readonly s3_uri?: string;
  readonly s3_key?: string;
  readonly s3_bucket?: string;
}
export interface StreamEventFiles {
  readonly type: 'files';
  readonly files: ReadonlyArray<StreamEventFile>;
}

/**
 * Structured event from NDJSON stream - Options type
 * Backend extracted a `<bops:options>` block from the agent's text and is
 * delivering the parsed items so the frontend can render clickable cards.
 */
export interface StreamEventChoice {
  readonly id: string;
  readonly label: string;
}
export interface StreamEventOptionItem {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  /** Only on `choicegrid` rows: the answers offered for that row. */
  readonly choices?: ReadonlyArray<StreamEventChoice>;
}
export interface StreamEventOptions {
  readonly type: 'options';
  readonly items: ReadonlyArray<StreamEventOptionItem>;
}

/**
 * Structured event from NDJSON stream - Multi-select type
 * Backend extracted a `<bops:multiselect>` block; the frontend renders
 * checkboxes with a confirm button (same item shape as options).
 */
export interface StreamEventMultiselect {
  readonly type: 'multiselect';
  readonly items: ReadonlyArray<StreamEventOptionItem>;
}

/**
 * Structured event from NDJSON stream - Choice grid type
 * Backend extracted a `<bops:choicegrid>` block: one question asked of many
 * subjects, each row rendered with its own answer set plus a confirm button.
 */
export interface StreamEventChoicegrid {
  readonly type: 'choicegrid';
  readonly items: ReadonlyArray<StreamEventOptionItem>;
}

/**
 * Union type of all possible stream events
 */
export type StreamEvent =
  | StreamEventChunk
  | StreamEventTrace
  | StreamEventProgress
  | StreamEventRetract
  | StreamEventDone
  | StreamEventError
  | StreamEventFiles
  | StreamEventOptions
  | StreamEventMultiselect
  | StreamEventChoicegrid;

/** Yields each NDJSON line of an agent run; an `error` event throws. */
async function* readNDJSONEvents(response: Response): AsyncGenerator<StreamEvent, void, undefined> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body available for streaming');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      // Process each complete line using while loop to comply with ESLint rules
      let lineIndex = 0;
      while (lineIndex < lines.length) {
        const trimmedLine = lines[lineIndex].trim();
        if (trimmedLine) {
          try {
            const event = JSON.parse(trimmedLine) as StreamEvent;

            if (event.type === 'error') {
              throw new Error(event.error || 'Streaming error');
            }

            yield event;
          } catch (parseError) {
            if (parseError instanceof SyntaxError) {
              // eslint-disable-next-line no-console
              console.warn('Failed to parse NDJSON line:', trimmedLine);
            } else {
              throw parseError;
            }
          }
        }
        lineIndex += 1;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

const createRunRequestBody = (request: AgentStreamRequest) => ({
  prompt: request.prompt,
  conversation_id: request.conversation_id || DEFAULT_CONVERSATION_ID,
  max_prompt_length: request.max_prompt_length || DEFAULT_MAX_PROMPT_LENGTH,
  trace: true,
  source_name: request.source_name,
  display_text: request.display_text,
  context_file: request.context_file,
  selected_journey: request.selected_journey,
  journey_cache_file: request.journey_cache_file,
  interaction_timestamp: request.interaction_timestamp,
});

/**
 * Async generator that streams agent events via NDJSON.
 * This is a simpler alternative to SSE for consuming streaming events.
 *
 * @param baseApiAddress - The base API address
 * @param request - The agent stream request parameters
 * @param signal - Optional AbortSignal for request cancellation
 * @yields {StreamEvent} Stream events in NDJSON format
 * @throws {Error} When connection fails or streaming errors occur
 */
export async function* streamAgentNDJSON(
  baseApiAddress: string,
  request: AgentStreamRequest,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent, void, undefined> {
  const accessToken = await getAccessToken();

  const response = await fetch(`${baseApiAddress}agent/aws-runner/run/stream-json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(createRunRequestBody(request)),
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  yield* readNDJSONEvents(response);
}

interface UploadErrorBody {
  detail?: { code?: unknown; file_name?: string | null };
}

const toUploadError = async (response: Response): Promise<Error> => {
  const body = (await response.json().catch(() => null)) as UploadErrorBody | null;
  const code = body?.detail?.code;
  if (typeof code === 'string') return new AttachmentUploadError(code, body?.detail?.file_name ?? null);
  return new Error(`HTTP error! status: ${response.status}`);
};

/**
 * Sends a message and its files in one multipart request. Resolves once the
 * core API took the files, with the same events as `streamAgentNDJSON`.
 *
 * @throws {AttachmentUploadError} When the core API refuses a file
 */
export async function streamAgentWithFiles(
  baseApiAddress: string,
  request: AgentStreamRequest,
  files: File[],
  signal?: AbortSignal,
): Promise<AsyncGenerator<StreamEvent, void, undefined>> {
  const accessToken = await getAccessToken();
  const body = new FormData();
  body.append('request', JSON.stringify(createRunRequestBody(request)));
  files.forEach((file) => body.append('files', file));

  // The core API checks the agent's flag from the query before it reads the body.
  const query = request.source_name ? `?source_name=${encodeURIComponent(request.source_name)}` : '';

  // No Content-Type: the browser adds the multipart boundary.
  const response = await fetch(`${baseApiAddress}agent/aws-runner/run/stream-upload${query}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body,
    signal,
  });

  if (!response.ok) throw await toUploadError(response);
  return readNDJSONEvents(response);
}

export { streamAgent as streamAgentSSE };
export default streamAgent;
